import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { PrimaryButton } from "@normasteaching/norma-ui";
import {
  BillingFormFields,
  BillingFormSection,
} from "../../NormasTeachingCheckout/BillingFormSection.tsx";
import {
  bonificoBillingSchemaFields,
  hasSdiOrPec,
} from "../../BonificoCheckout/bonificoBillingValidation.ts";
import {
  BonificoOrderDTO,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import {
  toBonificoBusinessInformation,
  toBonificoCustomerTaxCode,
} from "../../ProductService/BonificoOrder/toBonificoBusinessInformation.ts";
import {
  hasBonificoInvoiceBeenIssued,
  issuesInvoiceOnPayment,
  toBonificoBillingFormFields,
} from "../../ProductService/BonificoOrder/bonificoBilling.ts";
import {
  describeIssuedInvoiceVat,
  isArticle7Ter,
  vatExemptionFor,
} from "../../ProductService/BonificoOrder/article7Ter.ts";
import { Article7TerSwitch } from "./Article7TerSwitch.tsx";
import { useUpdateBonificoOrderBilling } from "../../ProductService/BonificoOrder/useUpdateBonificoOrderBilling.ts";
import { OverlayDialog } from "../../UI/Modal/OverlayDialog.tsx";

const schema = yup
  .object(bonificoBillingSchemaFields)
  .test("has-at-least-sdi-or-pec", "", hasSdiOrPec);

export function EditBonificoBillingModal({
  order,
  onSaved,
  onClose,
}: {
  order: BonificoOrderDTO;
  onSaved: (order: BonificoOrderDTO) => void;
  onClose: () => void;
}) {
  const { updateBilling, loading } = useUpdateBonificoOrderBilling();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isArticle7TerApplied, setIsArticle7TerApplied] = useState(
    isArticle7Ter(order.vatExemption),
  );
  const canChooseArticle7Ter = issuesInvoiceOnPayment(order);
  const issuedInvoiceVat = describeIssuedInvoiceVat(order);
  const {
    control,
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<BillingFormFields>({
    resolver: yupResolver(schema) as unknown as Resolver<BillingFormFields>,
    defaultValues: {
      ...toBonificoBillingFormFields(order),
      invoiceRequired: true,
    },
  });

  const onSubmit = async (values: BillingFormFields) => {
    setSubmitError(null);
    try {
      const updated = await updateBilling(order.orderId, {
        businessInformation: toBonificoBusinessInformation(values),
        taxCode: toBonificoCustomerTaxCode(values),
        vatExemption: canChooseArticle7Ter
          ? vatExemptionFor(isArticle7TerApplied)
          : undefined,
      });
      onSaved(updated);
    } catch (e) {
      setSubmitError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante il salvataggio dei dati di fatturazione. Riprova.",
        ),
      );
    }
  };

  return (
    <OverlayDialog
      onClose={onClose}
      isClosable={!loading}
      panelClassName="bg-white rounded-2xl shadow-xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto flex flex-col gap-4"
    >
      <h2 className="text-xl font-bold">Modifica dati di fatturazione</h2>
      <p className="text-sm text-gray-600">
        {order.customer.firstName} {order.customer.lastName} ·{" "}
        {order.customer.email}
      </p>
      {hasBonificoInvoiceBeenIssued(order) && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          La fattura è già stata emessa con i dati precedenti e su Fatture in
          Cloud non cambia: i dati corretti valgono per le note di credito dei
          rimborsi successivi.
        </p>
      )}
      <BillingFormSection
        control={control}
        register={register}
        errors={errors}
        invoiceRequired={true}
        businessType={watch("businessType")}
        taxCode={watch("taxCode")}
        isBusinessInformationRequired={true}
        isInvoiceMandatory={true}
        trigger={trigger}
        askBillingCountry
        onArticle7TerSuggestion={setIsArticle7TerApplied}
      />
      {canChooseArticle7Ter && (
        <div className="flex flex-col gap-1">
          <Article7TerSwitch
            checked={isArticle7TerApplied}
            onChange={setIsArticle7TerApplied}
          />
          {issuedInvoiceVat && (
            <p className="text-xs text-gray-500">
              {issuedInvoiceVat} Le note di credito dei rimborsi useranno la
              stessa modalità, anche se cambi la spunta.
            </p>
          )}
        </div>
      )}
      {submitError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {submitError}
        </p>
      )}
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
        >
          Annulla
        </button>
        <PrimaryButton
          label="Salva"
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          disabled={loading}
        />
      </div>
    </OverlayDialog>
  );
}
